import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { getMatomoConfig } from "@/lib/matomo/config";
import {
  readMatomoConsent,
  type MatomoConsentState,
} from "@/lib/matomo/consent";

type MatomoConsentUiValue = {
  enabled: boolean;
  consent: MatomoConsentState;
  bannerOpen: boolean;
  openBanner: () => void;
  closeBanner: () => void;
  setConsentState: (state: MatomoConsentState) => void;
};

const MatomoConsentUiContext = createContext<MatomoConsentUiValue | null>(null);

export function MatomoConsentUiProvider({ children }: { children: ReactNode }) {
  const enabled = getMatomoConfig().enabled;
  const [consent, setConsent] = useState<MatomoConsentState>(() =>
    enabled ? readMatomoConsent() : "denied"
  );
  const [forceOpen, setForceOpen] = useState(false);

  const bannerOpen = enabled && (consent === "unknown" || forceOpen);

  const openBanner = useCallback(() => {
    if (!enabled) {
      return;
    }
    setForceOpen(true);
  }, [enabled]);

  const closeBanner = useCallback(() => {
    setForceOpen(false);
  }, []);

  const setConsentState = useCallback((state: MatomoConsentState) => {
    setConsent(state);
    setForceOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      enabled,
      consent,
      bannerOpen,
      openBanner,
      closeBanner,
      setConsentState,
    }),
    [enabled, consent, bannerOpen, openBanner, closeBanner, setConsentState]
  );

  return (
    <MatomoConsentUiContext.Provider value={value}>
      {children}
    </MatomoConsentUiContext.Provider>
  );
}

export function useMatomoConsentUi(): MatomoConsentUiValue {
  const ctx = useContext(MatomoConsentUiContext);
  if (!ctx) {
    throw new Error(
      "useMatomoConsentUi must be used within MatomoConsentUiProvider"
    );
  }
  return ctx;
}
